import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import { LUMINA_CREATORS } from '../components/FifaPlayerCard';
import { useWaveCart } from '../contexts/WaveCartContext';
import { useBrandProfile } from '../contexts/BrandProfileContext';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft } from 'lucide-react';

import { Stepper } from '../components/wave-builder/SharedUI';
import { BG, CARD, BORDER, LUMINA_SKUS } from '../components/wave-builder/constants';
import { EST_CREATOR_FLOOR } from '../components/wave-builder/calibrationProfiles';
import { APPLY_TEMPLATES } from '../components/wave-builder/applyTemplates';
import { useWaveRfpActions } from '../components/wave-builder/useWaveRfpActions';
import { useIsDemoBrand } from '../lib/demoBrand';
import { useDemoProfile } from '../hooks/useDemoProfile';

import Step1Overview from '../components/wave-builder/Step1Overview';
import Step2Cohort from '../components/wave-builder/Step2Cohort';
import Step3Review from '../components/wave-builder/Step3Review';
import SkuRecommendationsModal from '../components/wave-builder/SkuRecommendationsModal';
import RfpPreviewModal from '../components/wave-builder/RfpPreviewModal';
import ReadyToFirePanel from '../components/wave-builder/ReadyToFirePanel';
// eslint-disable-next-line no-unused-vars
import NextMovePanel from '../components/brand/NextMovePanel';
// eslint-disable-next-line no-unused-vars
import TrendsPanel from '../components/brand/TrendsPanel';
import BrandProfileModal from '../components/brand/BrandProfileModal';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BuildWavePage
 * Thin orchestrator: owns form + RFP state and routes between extracted step components.
 * The complex UI logic lives inside components/wave-builder/*.
 */
export default function BuildWavePage() {
  const { user } = useAuth();
  const isDemoBrand = useIsDemoBrand();
  const brandScale = user?.brand_scale || 'dtc_national';
  // Scale-aware geo default — single-location + multi-location brands start on
  // DMA/Zip so they don't have to back out of a wasteful "National" default.
  const defaultGeo = (brandScale === 'single_location' || brandScale === 'multi_location')
    ? 'DMA'
    : 'National';

  const [step, setStep] = useState(1);
  const [waveType, setWaveType] = useState(null);

  // Common fields
  const [campaignName, setCampaignName] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  const [campaignGoal, setCampaignGoal] = useState('');
  const [budget, setBudget] = useState('250000');
  const [startDate, setStartDate] = useState('2026-06-01');
  const [endDate, setEndDate] = useState('2026-07-15');
  const [submissionDeadline, setSubmissionDeadline] = useState('2026-05-25');
  const [platforms, setPlatforms] = useState([]);

  // Geo-targeting
  const [geoLevel, setGeoLevel] = useState(defaultGeo);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedDmas, setSelectedDmas] = useState([]);
  const [zipCodes, setZipCodes] = useState('');

  // Performance
  const [commissionPct, setCommissionPct] = useState('10%');
  const [customCommission, setCustomCommission] = useState('');
  const [boostTrack, setBoostTrack] = useState('none');
  const [boostFields, setBoostFields] = useState({});
  const [bonusType, setBonusType] = useState('$X per 100 units sold');
  const [bonusDetails, setBonusDetails] = useState('$5 per 100 units sold via ShopMy');
  const [enabledPerfKpis, setEnabledPerfKpis] = useState({});
  const [selectedDeliverables, setSelectedDeliverables] = useState(new Set());
  const [openPlatformTab, setOpenPlatformTab] = useState('');

  // Branding
  const [enabledBrandKpis, setEnabledBrandKpis] = useState({});
  const [primaryCreative, setPrimaryCreative] = useState('');
  const [secondaryCreatives, setSecondaryCreatives] = useState(new Set());
  const [creativeNotes, setCreativeNotes] = useState('');
  const [openBrandTab, setOpenBrandTab] = useState('');

  // Cohort
  const { selectedCreators: cartCreators, clearCart } = useWaveCart();
  // Demo brands keep the Lumina-style starter cohort. Real signups start
  // empty and pull in creators from Discover or Wave Cart.
  const [selectedCreators, setSelectedCreators] = useState(
    new Set(isDemoBrand ? LUMINA_CREATORS.map(c => c.id) : [])
  );
  const [generatingCohort, setGeneratingCohort] = useState(false);
  const scrollRef = useRef(null);

  // First-visit onboarding: prompt for primary vertical if not set
  const { primaryVertical, loading: profileLoading, skus: brandSkus } = useBrandProfile();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  useEffect(() => {
    if (!profileLoading && !primaryVertical) setOnboardingOpen(true);
  }, [profileLoading, primaryVertical]);

  // ═══ Smart Intelligence "Apply" hand-off ═══
  // Insights & Analytics deep-links here with URL params like:
  //   /build-wave?apply=duplicate&source=w2
  //   /build-wave?apply=refilter-tier&tier=micro
  //   /build-wave?apply=platform-mix&emphasis=instagram
  //   /build-wave?apply=sleep-niche
  //   /build-wave?apply=tighten-burst
  // We read those once on mount, pre-fill the form, and show a banner
  // explaining which recommendation was just applied.
  const location = useLocation();
  const navigate = useNavigate();
  const [appliedRecommendation, setAppliedRecommendation] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const apply = params.get('apply');
    if (!apply) return;

    const template = APPLY_TEMPLATES[apply];
    if (!template) return;
    if (template.waveType) setWaveType(template.waveType);
    if (template.campaignName) setCampaignName(template.campaignName);
    if (template.campaignGoal) setCampaignGoal(template.campaignGoal);
    if (template.budget) setBudget(template.budget);
    if (template.platforms) setPlatforms(template.platforms);
    if (template.commissionPct) setCommissionPct(template.commissionPct);
    if (template.boostTrack) setBoostTrack(template.boostTrack);
    if (template.boostFields) setBoostFields(template.boostFields);
    setAppliedRecommendation(template.banner);
    // Clear the query string after applying so reloads don't re-trigger.
    window.history.replaceState({}, '', location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter 58: Opportunity Radar → Spawn Wave hand-off.
  // The Radar / ReadyToFire panel passes `location.state.readyFirePreset` so
  // we can drop the brand *past* the Branding/Performance framing step.
  // Pre-fills waveType + goal + platforms + campaignName so the hero
  // selector collapses on arrival.
  useEffect(() => {
    const preset = location.state?.readyFirePreset;
    if (!preset) return;
    const title  = location.state?.readyFireTitle;
    if (preset.wave_type) setWaveType(preset.wave_type);
    if (preset.goal)      setCampaignGoal(preset.goal);
    if (preset.platforms) setPlatforms(preset.platforms);
    if (title)            setCampaignName(`${title} Wave`);
    if (preset.banner) {
      setAppliedRecommendation(preset.banner);
    } else if (title) {
      setAppliedRecommendation({
        kicker: 'Spawned from Opportunity Radar',
        title: `${title} — ${preset.wave_type || 'Wave'} context pre-filled`,
        detail: 'We skipped the Branding vs Performance step because the trend already tells us the angle. Tweak below and hit Generate Cohort.',
      });
    }
    // Wipe the router state so a manual refresh or Back/Forward doesn't re-apply.
    window.history.replaceState({}, '', location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { profile: demoProfile, isDemo } = useDemoProfile();

  const skuOptions = (brandSkus && brandSkus.length > 0)
    ? brandSkus.map((s, i) => ({ id: s.id || `brand-sku-${i}`, name: s.name, category: s.category || '', image: s.image }))
    // Demo brands get their own brand-specific SKU palette. Falls back to
    // the legacy Lumina catalogue only when no demo profile is available.
    : (isDemo && demoProfile?.build_wave_skus?.length)
      ? demoProfile.build_wave_skus
      : (isDemoBrand ? LUMINA_SKUS : []);

  // RFP modal + Wave Orchestrator preview state encapsulated in
  // useWaveRfpActions so this page stays focused on form orchestration.
  const selectedSKUObj = skuOptions.find(s => s.id === selectedSku);
  const selectedCreatorList = LUMINA_CREATORS.filter(c => selectedCreators.has(c.id));

  // ═══ Build-Group A — structured Creative Brief ═══
  // The brief is REQUIRED at /generate-rfps (backend Pydantic enforces it).
  // Shape: SSP-canonical v1, fingerprint f424908b…1af9e27d. Across a
  // ladder of waves the brief structure is FIXED — only the *content*
  // varies. `briefClonedFrom` is the provenance chip so the operator
  // can prove which wave the brief mirrors.
  // Declared BEFORE useWaveRfpActions so the hook can read it in its
  // closure — temporal-dead-zone otherwise.
  const [brief, setBrief] = useState({
    goal: '',
    format_spec: {
      aspect_ratio: '9:16',
      duration_seconds_max: 60,
      hook_first_seconds: 3,
      voiceover_required: false,
      b_roll_required: false,
    },
    message_points: [''],
    creative_freedom: '',
  });
  const [briefClonedFrom, setBriefClonedFrom] = useState(null);
  const handleBriefChange = useCallback((next) => {
    setBrief(next);
    // Any manual edit clears the cloned-from provenance — the operator
    // is no longer authoring an identical-to-X brief.
    setBriefClonedFrom(null);
  }, []);
  const handleBriefCloneSet = useCallback((meta) => {
    setBriefClonedFrom(meta);
  }, []);

  const rfpActions = useWaveRfpActions({
    campaignGoal,
    budget,
    platforms,
    selectedCreatorList,
    selectedCreatorsCount: selectedCreators.size,
    brief,
    briefClonedFrom,
    navigate,
  });
  const {
    showRfpModal, rfpLoading, rfpSending, rfpSent, rfpError,
    generatedRfps, waveId, footprintPulled, footprintSummary,
    burstPlan, burstPlanLoading, burstPlanError,
    preflight, preflightLoading,
    handleGenerateRfps, handleSendRfps, handlePreviewBurstPlan, handleRfpModalChange,
    runPreflight,
  } = rfpActions;

  // ═══ KPI Benchmarks — numeric targets that ship with RFP + measured live ═══
  const [kpiBenchmarks, setKpiBenchmarks] = useState([]);

  // Persist benchmarks when wave is created (waveId becomes available)
  useEffect(() => {
    if (!waveId || kpiBenchmarks.length === 0) return;
    (async () => {
      try {
        await axios.put(`${API}/api/brand/waves/${waveId}/kpi-benchmarks`,
          { benchmarks: kpiBenchmarks.map(b => ({ key: b.key, label: b.label, unit: b.unit, target: Number(b.target), comparator: '>=' })) },
          { withCredentials: true });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.warn('kpi benchmarks persist failed', err);
      }
    })();
  }, [waveId, kpiBenchmarks]);

  // Merge cart creators into selectedCreators on mount
  useEffect(() => {
    if (cartCreators.length > 0) {
      setSelectedCreators(prev => {
        const next = new Set(prev);
        cartCreators.forEach(c => next.add(c.id));
        return next;
      });
      clearCart();
    }
  }, [cartCreators, clearCart]);

  // ═══ SKU click magic moment ═══
  // The first time a brand picks a SKU on this Build Wave session, open
  // the recommendations panel. Re-clicking the same SKU re-opens it (so
  // the brand can reapply learnings without leaving the page). Switching
  // to a different SKU also re-opens. Suppressed only while the modal
  // is already open OR when the brand explicitly closed it for the
  // current sku (we track `skuRecoDismissedFor`).
  const [skuRecoOpen, setSkuRecoOpen] = useState(false);
  const [skuRecoDismissedFor, setSkuRecoDismissedFor] = useState(null);
  const [skuLearnings, setSkuLearnings] = useState(null);
  useEffect(() => {
    if (!selectedSku) return;
    if (skuRecoDismissedFor === selectedSku) return;
    setSkuRecoOpen(true);
  }, [selectedSku, skuRecoDismissedFor]);

  // Brand subscription tier — gates depth of recommendations + auto-apply.
  const userTier = (user?.subscription_tier || 'starter').toLowerCase();

  // Apply handler: hot-wires the existing wave-builder state so the brand
  // doesn't have to fill anything manually. Each kind maps to a state
  // setter; "all" applies everything we have.
  const applySkuLearning = useCallback((kind, payload) => {
    setSkuLearnings({ kind, payload, appliedAt: Date.now() });
    if (!payload) return;
    const applyCreators = (list) => {
      if (!Array.isArray(list) || !list.length) return;
      // Map historical creators onto the cohort by handle. We don't have
      // their internal IDs from this payload, so we tag the selectedCreators
      // set with a synthetic key and let the cohort step's "Add from
      // learnings" pill render them visually.
      // (Step2Cohort reads window.__skuLearningsCreators for compatibility.)
      window.__skuLearningsCreators = list;
    };
    const applyAngle = (angle) => {
      if (!angle?.angle) return;
      // Angle pre-fill lives on the creative-brief; surface it in the
      // notes field so Step2's review screen can pick it up.
      setCreativeNotes((prev) => {
        const tag = `Top angle: ${angle.angle.toUpperCase()}`;
        return prev?.includes(tag) ? prev : [tag, prev].filter(Boolean).join('\n');
      });
    };
    const applyCaption = (cap) => {
      if (!cap?.text) return;
      setPrimaryCreative((prev) => prev || cap.text);
    };
    const applyTiming = (timing) => {
      if (!timing?.platform_mix) return;
      const inferred = Object.entries(timing.platform_mix || {})
        .filter(([, v]) => v >= 0.15)
        .map(([k]) => k);
      if (inferred.length) setPlatforms((prev) => Array.from(new Set([...(prev || []), ...inferred])));
    };
    if (kind === 'all') {
      applyCreators(payload.creators);
      applyAngle(payload.angles?.[0]);
      applyCaption(payload.captions?.[0]);
      applyTiming(payload.timing);
    } else if (kind === 'creators') applyCreators(payload);
    else if (kind === 'angle') applyAngle(payload);
    else if (kind === 'caption') applyCaption(payload);
    else if (kind === 'timing') applyTiming(payload);
  }, []);

  const togglePlatform = useCallback((p) => {
    setPlatforms(prev => (prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]));
    setOpenPlatformTab(prev => prev || p);
    setOpenBrandTab(prev => prev || p);
  }, []);

  const togglePerfKpi = useCallback((platform, kpiName) => {
    setEnabledPerfKpis(prev => {
      const key = `${platform}::${kpiName}`;
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  const toggleBrandKpi = useCallback((platform, kpiName) => {
    setEnabledBrandKpis(prev => {
      const key = `${platform}::${kpiName}`;
      return { ...prev, [key]: !prev[key] };
    });
  }, []);

  const totalKpiCount = Object.values(waveType === 'Performance' ? enabledPerfKpis : enabledBrandKpis).filter(Boolean).length;
  const estCreators = Math.max(EST_CREATOR_FLOOR, Math.round(Number(budget) / 1000));

  // Split the progress calculation into 3 sub-memos so each has <=5 dependencies
  // and only recomputes when its own slice changes.
  const corePogress = useMemo(() => {
    let filled = 0;
    if (campaignName) filled++;
    if (selectedSku) filled++;
    if (campaignGoal) filled++;
    if (budget) filled++;
    return filled;
  }, [campaignName, selectedSku, campaignGoal, budget]);

  const flightProgress = useMemo(() => {
    let filled = 0;
    if (startDate && endDate) filled++;
    if (platforms.length > 0) filled++;
    if (geoLevel) filled++;
    return filled;
  }, [startDate, endDate, platforms, geoLevel]);

  const typeProgress = useMemo(() => {
    if (waveType === 'Performance') {
      return { filled: (commissionPct ? 1 : 0) + (selectedDeliverables.size > 0 ? 1 : 0), extraTotal: 2 };
    }
    if (waveType === 'Branding') {
      return { filled: (primaryCreative ? 1 : 0) + (totalKpiCount > 0 ? 1 : 0), extraTotal: 2 };
    }
    return { filled: 0, extraTotal: 0 };
  }, [waveType, commissionPct, selectedDeliverables.size, primaryCreative, totalKpiCount]);

  const progress = useMemo(() => {
    const filled = corePogress + flightProgress + typeProgress.filled;
    const total = 7 + typeProgress.extraTotal;
    return Math.round((filled / total) * 100);
  }, [corePogress, flightProgress, typeProgress]);

  const handleGenerateCohort = () => {
    setGeneratingCohort(true);
    setTimeout(() => { setGeneratingCohort(false); setStep(2); }, 2200);
  };

  // ═══ Smart Apply-to-Brief ═══
  // Populate form fields from a tentpole moment.
  const handleApplyMoment = (payload) => {
    if (!campaignName) {
      setCampaignName(`${payload.momentName} Wave`);
    }
    if (payload.startDate && !startDate) setStartDate(payload.startDate);
    if (payload.endDate && !endDate) setEndDate(payload.endDate);
    if (payload.shopMyHook && !campaignGoal) setCampaignGoal(payload.shopMyHook);
    if (payload.bestFormats?.length > 0) {
      const platformMap = { TikTok: ['GRWM', 'Duet', 'Stitch'], Instagram: ['Reel', 'Story', 'GRWM'], YouTube: ['Video', 'Tutorial'] };
      payload.bestFormats.forEach(fmt => {
        Object.entries(platformMap).forEach(([plat, formats]) => {
          if (formats.some(f => fmt.toLowerCase().includes(f.toLowerCase())) && !platforms.includes(plat)) {
            setPlatforms(prev => prev.includes(plat) ? prev : [...prev, plat]);
          }
        });
      });
    }
  };

  // ═══ Smart Cohort Pre-seed ═══
  // When a tentpole is activated, the returned creators auto-populate the cohort
  // and jump the wizard forward to Step 2.
  const handleActivated = (creators) => {
    if (!Array.isArray(creators) || creators.length === 0) return;
    const handles = new Set(creators.map(c => c.handle?.toLowerCase()));
    setSelectedCreators(prev => {
      const next = new Set(prev);
      // match LUMINA_CREATORS by handle (case-insensitive) and add to cohort
      LUMINA_CREATORS.forEach(c => {
        if (handles.has(c.handle?.toLowerCase())) next.add(c.id);
      });
      return next;
    });
    setStep(2);
  };

  // ═══ Apply the "Your Next Move" recommendation to the Plan form ═══
  // Legacy — kept for TentpoleMomentsSection apply hook (unused in new panel path).
  // eslint-disable-next-line no-unused-vars
  const handleApplyRecommendation = (rec) => {
    if (!campaignName && rec?.moment_name) setCampaignName(`${rec.moment_name} Wave`);
    if (rec?.suggested_budget && !budget) setBudget(String(rec.suggested_budget));
    if (rec?.vertical && !campaignGoal) {
      setCampaignGoal(`Activate creators around ${rec.moment_name}.`);
    }
    if (!waveType) setWaveType('Performance');
  };

  // Apply a Ready-to-Fire preset — pre-fills Step 1 fields so the rep
  // can tweak and launch in under a minute.
  const handleApplyReadyFirePreset = (preset, item) => {
    if (!preset) return;
    if (preset.wave_type)     setWaveType(preset.wave_type);
    if (preset.goal)          setCampaignGoal(preset.goal);
    if (preset.platforms)     setPlatforms(preset.platforms);
    if (!campaignName && item?.title) setCampaignName(`${item.title} Wave`);
  };

  const contextStrip = (
    <ReadyToFirePanel onApplyPreset={handleApplyReadyFirePreset} />
  );
  const formState = {
    waveType, campaignName, selectedSku, campaignGoal, budget, startDate, endDate, submissionDeadline, platforms,
    geoLevel, selectedStates, selectedDmas, zipCodes,
    commissionPct, customCommission, boostTrack, boostFields, bonusType, bonusDetails, enabledPerfKpis, selectedDeliverables, openPlatformTab,
    enabledBrandKpis, primaryCreative, secondaryCreatives, creativeNotes, openBrandTab,
    waveId, waveStatus: rfpSent ? 'sent' : (waveId ? 'draft' : null),
  };
  const formActions = {
    setWaveType, setCampaignName, setSelectedSku, setCampaignGoal, setBudget, setStartDate, setEndDate, setSubmissionDeadline,
    setGeoLevel, setSelectedStates, setSelectedDmas, setZipCodes,
    setCommissionPct, setCustomCommission, setBoostTrack, setBoostFields, setBonusType, setBonusDetails, setSelectedDeliverables, setOpenPlatformTab,
    setPrimaryCreative, setSecondaryCreatives, setCreativeNotes, setOpenBrandTab,
    togglePlatform, togglePerfKpi, toggleBrandKpi,
  };

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 max-w-[1500px] mx-auto relative" style={{ background: BG }} data-testid="build-wave-page">
        <span className="absolute top-3 right-5 text-[7px] text-gray-700 uppercase tracking-widest">Patent Pending</span>

        <div className="mb-2">
          {step > 1 && (
            <button onClick={() => setStep(s => Math.max(s - 1, 1))} className="mb-2 p-1.5 rounded-lg text-gray-500 hover:text-white transition-all" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="back-btn">
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <h1 className="text-2xl font-black text-white text-center mb-1">Plan New Wave</h1>
          <Stepper currentStep={step} />
        </div>

        {appliedRecommendation && (
          <div className="rounded-xl p-4 flex items-start gap-3 mb-5 relative overflow-hidden" data-testid="applied-recommendation-banner"
               style={{
                 background: appliedRecommendation.tone === 'pink'
                   ? 'linear-gradient(135deg, rgba(255,77,148,0.1), rgba(10,10,10,0.9))'
                   : 'linear-gradient(135deg, rgba(0,212,255,0.08), rgba(10,10,10,0.9))',
                 border: `1px solid ${appliedRecommendation.tone === 'pink' ? 'rgba(255,77,148,0.3)' : 'rgba(0,212,255,0.3)'}`,
               }}>
            <div className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
                 style={{ background: appliedRecommendation.tone === 'pink' ? 'rgba(255,77,148,0.15)' : 'rgba(0,212,255,0.15)' }}>
              <span style={{ color: appliedRecommendation.tone === 'pink' ? '#FF4D94' : '#00D4FF', fontSize: 18 }}>✨</span>
            </div>
            <div className="flex-1">
              <p className="text-xs font-black text-white">{appliedRecommendation.title}</p>
              <p className="text-[11px] text-gray-400 leading-relaxed mt-0.5">{appliedRecommendation.body}</p>
            </div>
            <button onClick={() => setAppliedRecommendation(null)}
                    data-testid="dismiss-recommendation-banner"
                    className="text-[10px] text-gray-500 hover:text-white px-2 py-1 rounded transition-colors">
              Dismiss
            </button>
          </div>
        )}

        {step === 1 && (
          <Step1Overview
            state={formState}
            actions={formActions}
            progress={progress}
            totalKpiCount={totalKpiCount}
            estCreators={estCreators}
            selectedSKUObj={selectedSKUObj}
            onGenerateCohort={handleGenerateCohort}
            generatingCohort={generatingCohort}
            onApplyMoment={handleApplyMoment}
            onActivated={handleActivated}
            contextStrip={contextStrip}
          />
        )}

        {step === 2 && (
          <Step2Cohort
            selectedCreators={selectedCreators}
            setSelectedCreators={setSelectedCreators}
            scrollRef={scrollRef}
            onProceed={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <Step3Review
            state={formState}
            selectedCreatorsCount={selectedCreators.size}
            totalKpiCount={totalKpiCount}
            selectedSKUObj={selectedSKUObj}
            onGenerateRfps={handleGenerateRfps}
            burstPlan={burstPlan}
            burstPlanLoading={burstPlanLoading}
            burstPlanError={burstPlanError}
            onPreviewBurstPlan={handlePreviewBurstPlan}
            canPreview={selectedCreators.size > 0 && !!campaignGoal}
            kpiBenchmarks={kpiBenchmarks}
            onKpiBenchmarksChange={setKpiBenchmarks}
            brief={brief}
            onBriefChange={handleBriefChange}
            briefClonedFrom={briefClonedFrom}
            onBriefCloneSet={handleBriefCloneSet}
          />
        )}

        <RfpPreviewModal
          open={showRfpModal}
          onOpenChange={handleRfpModalChange}
          rfpLoading={rfpLoading}
          rfpError={rfpError}
          rfpSent={rfpSent}
          rfpSending={rfpSending}
          generatedRfps={generatedRfps}
          footprintPulled={footprintPulled}
          footprintSummary={footprintSummary}
          onSendRfps={handleSendRfps}
          preflight={preflight}
          preflightLoading={preflightLoading}
          onRunPreflight={runPreflight}
          waveId={waveId}
        />

        <BrandProfileModal open={onboardingOpen} onOpenChange={setOnboardingOpen} isOnboarding />

        {/* SKU click magic moment — opens the moment a brand picks a SKU. */}
        <SkuRecommendationsModal
          sku={selectedSKUObj}
          open={skuRecoOpen && !!selectedSKUObj}
          onClose={() => {
            setSkuRecoOpen(false);
            // Remember the dismissal so a render-loop doesn't re-open on
            // the same SKU; re-selecting clears this in the effect above.
            if (selectedSku) setSkuRecoDismissedFor(selectedSku);
          }}
          onApply={applySkuLearning}
          userTier={userTier}
        />
      </div>
    </DashboardLayout>
  );
}
